import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Box,
    Alert,
    CircularProgress,
    Typography,
    Divider,
    Tabs,
    Tab,
    Autocomplete,
    Chip,
    InputAdornment,
    IconButton,
    Tooltip,
    Collapse,
} from "@mui/material";
import {
    CameraAlt as CameraIcon,
    LocalOffer as SaleIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { BarcodeScannerDialog } from "../common/BarcodeScannerDialog";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Product, Category, Brand } from "../../types";
import { ProductImage } from "../../types/productImage";
import { productService } from "../../services/productService";
import { productImageService } from "../../services/productImageService";
import { campaignService } from "../../services/campaignService";
import { Link as RouterLink } from "react-router-dom";
import { SelectWithCreate } from "../common/SelectWithCreate";
import { CreateCategoryModal } from "../common/CreateCategoryModal";
import { CreateBrandModal } from "../common/CreateBrandModal";
import { ImageUploadZone } from "../product/ImageUploadZone";
import { ProductImageGallery } from "../product/ProductImageGallery";
import { ProductSlugHint } from "../products/ProductSlugHint";
import { PRODUCT_SLUG_MAX, SLUG_RE, cleanSlugInput, productSlugFromName } from "../../utils/slug";

const schema = yup.object({
    name: yup.string().required("Product name is required"),
    slug: yup
        .string()
        .optional()
        .max(PRODUCT_SLUG_MAX, `Keep it under ${PRODUCT_SLUG_MAX} characters`)
        .test("slug", "Use lowercase letters, numbers and single hyphens", (v) => !v || SLUG_RE.test(v)),
    description: yup.string().optional(),
    category_id: yup.string().required("Category is required"),
    brand_id: yup.string().optional(),
    sku: yup.string().required("SKU is required"),
    barcode: yup.string().optional(),
    selling_price: yup
        .number()
        .positive("Selling price must be positive")
        .required("Selling price is required"),
    cost_price: yup
        .number()
        .positive("Cost price must be positive")
        .required("Cost price is required"),
    sale_price: yup
        .number()
        .positive("Sale price must be positive")
        .test(
            "less-than-selling",
            "Sale price must be less than the selling price",
            function (value) {
                if (value === undefined || value === null) return true;
                const { selling_price } = this.parent;
                return selling_price === undefined || value < selling_price;
            },
        )
        .optional(),
    sale_starts_at: yup.date().nullable().optional(),
    sale_ends_at: yup
        .date()
        .nullable()
        .optional()
        .test(
            "after-start",
            "Sale end must be after the sale start",
            function (value) {
                if (!value) return true;
                const { sale_starts_at } = this.parent;
                if (!sale_starts_at) return true;
                return value > sale_starts_at;
            },
        ),
    weight: yup.number().min(0, "Net weight cannot be negative").optional(),
    gross_weight: yup.number().min(0, "Gross weight cannot be negative").optional(),
    dimensions: yup.string().optional(),
    initial_stock: yup
        .number()
        .min(0, "Initial stock cannot be negative")
        .optional(),
    min_stock_level: yup
        .number()
        .min(0, "Minimum stock level cannot be negative")
        .optional(),
    max_stock_level: yup
        .number()
        .min(1, "Maximum stock level must be at least 1")
        .optional(),
    is_active: yup.boolean().optional(),
    is_featured: yup.boolean().optional(),
    is_visible: yup.boolean().optional(),
});

interface ProductFormData {
    name: string;
    /** The web address (/products/<slug>). */
    slug?: string;
    description?: string;
    category_id: string;
    brand_id?: string;
    sku: string;
    barcode?: string;
    selling_price: number;
    cost_price: number;
    sale_price?: number;
    sale_starts_at?: Date | null;
    sale_ends_at?: Date | null;
    weight?: number;
    gross_weight?: number;
    dimensions?: string;
    initial_stock?: number;
    min_stock_level?: number;
    max_stock_level?: number;
    is_active?: boolean;
    is_featured?: boolean;
    is_visible?: boolean;
}

interface ProductFormProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (product: Product) => void;
    product?: Product | null;
    mode: "create" | "edit";
}

const formatCampaignPrice = (n: number) => `৳${Number(n).toLocaleString("en-BD")}`;

export const ProductForm: React.FC<ProductFormProps> = ({
    open,
    onClose,
    onSubmit,
    product,
    mode,
}) => {
    const nameInputRef = useRef<HTMLInputElement | null>(null);
    // The campaign whose price this product's sale is, if any.
    const [saleCampaign, setSaleCampaign] = useState<{ id: string; name: string; ends: string | null } | null>(null);
    useEffect(() => {
        setSaleCampaign(null);
        const campaignId = open ? product?.sale_campaign_id : null;
        if (!campaignId) return;
        let cancelled = false;
        campaignService
            .list()
            .then((all) => {
                const c = all.find((x) => x.campaign_id === campaignId);
                if (!cancelled && c && c.status !== "ended") {
                    setSaleCampaign({ id: c.campaign_id, name: c.name, ends: c.ends_at });
                }
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [open, product?.sale_campaign_id]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [showCreateCategory, setShowCreateCategory] = useState(false);
    const [showCreateBrand, setShowCreateBrand] = useState(false);
    const [skuAuto, setSkuAuto] = useState(true);
    // A new product's web address follows its name until staff type their own.
    const [slugEdited, setSlugEdited] = useState(false);
    // Collapsed by default — the common case has no discount, so the form
    // stays uncluttered. Expanded automatically when editing a product
    // that already has one.
    const [discountEnabled, setDiscountEnabled] = useState(false);

    // Image management state
    const [currentTab, setCurrentTab] = useState(0);
    const [productImages, setProductImages] = useState<ProductImage[]>([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [additionalCategoryIds, setAdditionalCategoryIds] = useState<
        string[]
    >([]);
    const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm<ProductFormData>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            name: "",
            description: "",
            category_id: "",
            brand_id: "",
            sku: "",
            selling_price: 0,
            cost_price: 0,
            sale_price: undefined,
            sale_starts_at: null,
            sale_ends_at: null,
            weight: 0,
            gross_weight: 0,
            dimensions: "",
            initial_stock: 0,
            min_stock_level: 10,
            max_stock_level: 1000,
            is_active: true,
            is_featured: false,
            is_visible: true,
        },
    });

    const nameValue = watch("name");
    const slugValue = watch("slug") || "";
    const savedSlug = mode === "edit" ? product?.slug || "" : "";
    // New products: the address follows the name until edited by hand.
    useEffect(() => {
        if (mode === "create" && !slugEdited) {
            setValue("slug", productSlugFromName(nameValue || ""));
        }
    }, [mode, slugEdited, nameValue, setValue]);
    const skuValue = watch("sku");

    const generateSku = (value: string) => {
        if (!value) return "";
        const base = value
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/-{2,}/g, "-")
            .replace(/^-|-$/g, "");
        return base.slice(0, 32);
    };

    const loadProductImages = useCallback(async (productId: string) => {
        try {
            setLoadingImages(true);
            const images =
                await productImageService.getProductImages(productId);
            setProductImages(images);
        } catch (err) {
            console.error("Failed to load product images:", err);
        } finally {
            setLoadingImages(false);
        }
    }, []);

    const loadFormData = useCallback(async () => {
        try {
            const [categoriesData, brandsData] = await Promise.all([
                productService.getCategories(),
                productService.getBrands(),
            ]);
            setCategories(categoriesData);
            setBrands(brandsData);

            // Load images if in edit mode
            if (mode === "edit" && product?.product_id) {
                loadProductImages(product.product_id);
            }
        } catch (err) {
            console.error("Failed to load form data:", err);
            setError("Failed to load categories and brands");
        }
    }, [mode, product, loadProductImages]);

    useEffect(() => {
        if (open) {
            loadFormData();
            if (product && mode === "edit") {
                setSlugEdited(true); // an existing address never follows renames
                reset({
                    name: product.name,
                    slug: product.slug || "",
                    description: product.description || "",
                    category_id: product.category_id,
                    brand_id: product.brand_id || "",
                    sku: product.sku,
                    barcode: product.barcode || "",
                    selling_price: product.selling_price,
                    cost_price: product.cost_price,
                    sale_price: product.sale_price ?? undefined,
                    sale_starts_at: product.sale_starts_at
                        ? new Date(product.sale_starts_at)
                        : null,
                    sale_ends_at: product.sale_ends_at
                        ? new Date(product.sale_ends_at)
                        : null,
                    weight: product.weight || 0,
                    gross_weight: product.gross_weight || 0,
                    dimensions: product.dimensions || "",
                    is_active: product.is_active,
                    is_featured: product.is_featured ?? false,
                    is_visible: product.is_visible ?? true,
                    // Don't include stock fields in edit mode
                });
                setDiscountEnabled(product.sale_price != null);
                // Initialize additional categories from product M2M
                const extraCatIds = (product.categories || [])
                    .map((c) => c.category_id)
                    .filter((id) => id !== product.category_id);
                setAdditionalCategoryIds(extraCatIds);
                setSkuAuto(false); // preserve existing SKU on edit
            } else if (product && mode === "create") {
                // Clone mode: pre-fill from source product but clear SKU for auto-generation
                setSlugEdited(false); // its own address, made from the name
                reset({
                    name: product.name,
                    description: product.description || "",
                    category_id: product.category_id,
                    brand_id: product.brand_id || "",
                    sku: "",
                    barcode: "",
                    selling_price: product.selling_price,
                    cost_price: product.cost_price,
                    weight: product.weight || 0,
                    gross_weight: product.gross_weight || 0,
                    dimensions: product.dimensions || "",
                    initial_stock: 0,
                    min_stock_level: 10,
                    max_stock_level: 1000,
                    is_active: product.is_active ?? true,
                    is_featured: product.is_featured ?? false,
                    is_visible: product.is_visible ?? true,
                });
                const extraCatIds = (product.categories || [])
                    .map((c) => c.category_id)
                    .filter((id) => id !== product.category_id);
                setAdditionalCategoryIds(extraCatIds);
                setSkuAuto(true); // auto-generate SKU for the clone
                // Deliberately not carried over — a clone is a new listing,
                // and silently putting it on sale without staff noticing
                // would be an easy mistake.
                setDiscountEnabled(false);
            } else {
                setSlugEdited(false);
                reset({
                    name: "",
                    description: "",
                    category_id: "",
                    brand_id: "",
                    sku: "",
                    barcode: "",
                    selling_price: 0,
                    cost_price: 0,
                    weight: 0,
                    dimensions: "",
                    initial_stock: 0,
                    min_stock_level: 10,
                    max_stock_level: 1000,
                    is_active: true,
                    is_featured: false,
                    is_visible: true,
                });
                setAdditionalCategoryIds([]);
                setSkuAuto(true);
                setDiscountEnabled(false);
            }
        }
    }, [open, product, mode, reset, loadFormData]);

    useEffect(() => {
        if (skuAuto) {
            const nextSku = generateSku(nameValue);
            if (nextSku !== skuValue) {
                setValue("sku", nextSku, {
                    shouldDirty: true,
                    shouldValidate: false,
                });
            }
        }
    }, [nameValue, skuAuto, skuValue, setValue]);

    const handleImageUpload = async (files: File[]) => {
        if (!product?.product_id) return;

        try {
            await productImageService.uploadMultipleImages(
                product.product_id,
                files,
            );
            await loadProductImages(product.product_id);
        } catch (err: any) {
            throw new Error(err.message || "Failed to upload images");
        }
    };

    const handleImageDelete = async (imageId: string) => {
        if (!product?.product_id) return;

        try {
            await productImageService.deleteImage(product.product_id, imageId);
            await loadProductImages(product.product_id);
        } catch (err) {
            console.error("Failed to delete image:", err);
            throw err;
        }
    };

    const handleSetDefaultImage = async (imageId: string) => {
        if (!product?.product_id) return;

        try {
            await productImageService.setDefaultImage(
                product.product_id,
                imageId,
            );
            await loadProductImages(product.product_id);
        } catch (err) {
            console.error("Failed to set default image:", err);
            throw err;
        }
    };

    const handleCategoryCreated = (newCategory: Category) => {
        setCategories((prev) => [...prev, newCategory]);
        // Auto-select the newly created category
        setValue("category_id", newCategory.category_id);
    };

    const handleBrandCreated = (newBrand: Brand) => {
        setBrands((prev) => [...prev, newBrand]);
        // Auto-select the newly created brand
        setValue("brand_id", newBrand.brand_id);
    };

    const handleFormSubmit = async (data: ProductFormData) => {
        if (discountEnabled && !data.sale_price) {
            setError("Enter a sale price, or collapse the discount section to remove it.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            // Transform data to match API requirements. The web address is sent
            // only when staff chose one (a new product's automatic one is made
            // by the server, which also adds -2 if it's taken) or changed it.
            const { slug: typedSlug, ...rest } = data;
            const sendSlug =
                !!typedSlug && (mode === "create" ? slugEdited : typedSlug !== savedSlug);
            const productData = {
                ...rest,
                ...(sendSlug ? { slug: typedSlug } : {}),
                brand_id: data.brand_id || undefined,
                description: data.description || "",
                dimensions: data.dimensions || "",
                is_active: data.is_active ?? true,
                is_featured: data.is_featured ?? false,
                is_visible: data.is_visible ?? true,
                // Explicit null (not omitted) clears an existing discount —
                // exclude_unset semantics on the backend need the key
                // actually present to act on it, not left out.
                sale_price: discountEnabled ? data.sale_price : null,
                sale_starts_at:
                    discountEnabled && data.sale_starts_at
                        ? data.sale_starts_at.toISOString()
                        : null,
                sale_ends_at:
                    discountEnabled && data.sale_ends_at
                        ? data.sale_ends_at.toISOString()
                        : null,
                category_ids:
                    additionalCategoryIds.length > 0
                        ? Array.from(
                              new Set([
                                  data.category_id,
                                  ...additionalCategoryIds,
                              ]),
                          )
                        : undefined,
            };

            let result: Product;
            if (mode === "create") {
                result = await productService.createProduct(productData);
            } else {
                result = await productService.updateProduct(
                    product!.product_id,
                    productData,
                );
            }

            onSubmit(result);
            onClose();
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            let message: string;
            if (typeof detail === "string") {
                message = detail;
            } else if (Array.isArray(detail)) {
                // Pydantic 422 validation errors: [{loc, msg, type}, ...]
                message = detail.map((e: any) => {
                    const field = Array.isArray(e.loc) ? e.loc.slice(1).join(" → ") : "";
                    return field ? `${field}: ${e.msg}` : e.msg;
                }).join("\n");
            } else {
                message = `Failed to ${mode} product`;
            }
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        reset();
        setError("");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            TransitionProps={{ onEntered: () => nameInputRef.current?.focus() }}
        >
            <DialogTitle>
                {mode === "edit"
                    ? "Edit Product"
                    : product
                      ? "Clone Product"
                      : "Add New Product"}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
                        {error}
                    </Alert>
                )}

                {mode === "edit" && (
                    <Tabs
                        value={currentTab}
                        onChange={(_, newValue) => setCurrentTab(newValue)}
                        sx={{ borderBottom: 1, borderColor: "divider" }}
                    >
                        <Tab label="Product Details" />
                        <Tab label={`Images (${productImages.length})`} />
                    </Tabs>
                )}

                {/* Product Details Tab */}
                {(mode === "create" || currentTab === 0) && (
                    <Box component="form" sx={{ mt: 1 }}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Product Name"
                                    inputRef={nameInputRef}
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                />
                            )}
                        />

                        <Controller
                            name="slug"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                        setSlugEdited(true);
                                        field.onChange(cleanSlugInput(e.target.value));
                                    }}
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    label="Web address"
                                    placeholder="dl-litter-lemon-5l"
                                    error={!!errors.slug}
                                    helperText={
                                        errors.slug?.message || (
                                            <ProductSlugHint
                                                slug={slugValue}
                                                productId={mode === "edit" ? product?.product_id : undefined}
                                                savedSlug={savedSlug || undefined}
                                                automatic={mode === "create" && !slugEdited}
                                                suggestion={productSlugFromName(nameValue || "")}
                                                onUseSuggestion={(next) => {
                                                    setSlugEdited(mode === "edit");
                                                    setValue("slug", next, { shouldValidate: true });
                                                }}
                                            />
                                        )
                                    }
                                    FormHelperTextProps={{ component: "div" } as any}
                                />
                            )}
                        />

                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    label="Description"
                                    multiline
                                    minRows={4}
                                    maxRows={16}
                                    error={!!errors.description}
                                    helperText={
                                        errors.description?.message ||
                                        "Supports Markdown: **bold**, _italic_, - lists, ## headings, [links](url), and tables."
                                    }
                                />
                            )}
                        />

                        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                            <Controller
                                name="category_id"
                                control={control}
                                render={({ field }) => (
                                    <SelectWithCreate
                                        label="Category"
                                        value={field.value}
                                        onChange={field.onChange}
                                        options={categories.map((cat) => ({
                                            value: cat.category_id,
                                            label: cat.name,
                                        }))}
                                        onCreateNew={() =>
                                            setShowCreateCategory(true)
                                        }
                                        error={!!errors.category_id}
                                        helperText={errors.category_id?.message}
                                        required
                                        createButtonText="Add New Category"
                                    />
                                )}
                            />

                            <Controller
                                name="brand_id"
                                control={control}
                                render={({ field }) => (
                                    <SelectWithCreate
                                        label="Brand"
                                        value={field.value || ""}
                                        onChange={field.onChange}
                                        options={brands.map((brand) => ({
                                            value: brand.brand_id,
                                            label: brand.name,
                                        }))}
                                        onCreateNew={() =>
                                            setShowCreateBrand(true)
                                        }
                                        error={!!errors.brand_id}
                                        helperText={errors.brand_id?.message}
                                        createButtonText="Add New Brand"
                                    />
                                )}
                            />
                        </Box>

                        {/* Additional Categories (Multi-select) */}
                        <Autocomplete
                            multiple
                            options={categories.filter(
                                (cat) =>
                                    cat.category_id !== watch("category_id"),
                            )}
                            getOptionLabel={(option) => option.name}
                            value={categories.filter((cat) =>
                                additionalCategoryIds.includes(cat.category_id),
                            )}
                            onChange={(_, newValue) => {
                                setAdditionalCategoryIds(
                                    newValue.map((cat) => cat.category_id),
                                );
                            }}
                            isOptionEqualToValue={(option, value) =>
                                option.category_id === value.category_id
                            }
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        variant="outlined"
                                        label={option.name}
                                        size="small"
                                        {...getTagProps({ index })}
                                        key={option.category_id}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    margin="normal"
                                    fullWidth
                                    label="Additional Categories (Optional)"
                                    placeholder="Select additional categories..."
                                    helperText="Product will also appear in these categories"
                                />
                            )}
                        />

                        <Controller
                            name="sku"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="SKU"
                                    onChange={(e) => {
                                        setSkuAuto(false);
                                        field.onChange(e);
                                    }}
                                    error={!!errors.sku}
                                    helperText={errors.sku?.message}
                                />
                            )}
                        />

                        <Controller
                            name="barcode"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    label="Barcode (Optional)"
                                    placeholder="e.g. 8901234567890"
                                    helperText="Used for barcode scanner lookup at POS"
                                    error={!!errors.barcode}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title="Scan with camera">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setBarcodeScannerOpen(true)}
                                                        edge="end"
                                                    >
                                                        <CameraIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <BarcodeScannerDialog
                            open={barcodeScannerOpen}
                            onClose={() => setBarcodeScannerOpen(false)}
                            onScan={(barcode) => {
                                setValue("barcode", barcode, { shouldDirty: true });
                                setBarcodeScannerOpen(false);
                            }}
                            title="Scan Product Barcode"
                        />

                        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                            <Controller
                                name="selling_price"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        margin="normal"
                                        required
                                        fullWidth
                                        label="Selling Price (৳)"
                                        inputProps={{ step: 0.01, min: 0 }}
                                        error={!!errors.selling_price}
                                        helperText={
                                            errors.selling_price?.message
                                        }
                                    />
                                )}
                            />

                            <Controller
                                name="cost_price"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        margin="normal"
                                        fullWidth
                                        label="Cost Price"
                                        inputProps={{ step: 0.01, min: 0 }}
                                        error={!!errors.cost_price}
                                        helperText={errors.cost_price?.message}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ mt: 1 }}>
                            {saleCampaign && (
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    During the campaign{" "}
                                    <b>
                                        <RouterLink to={`/campaigns/${saleCampaign.id}`}>{saleCampaign.name}</RouterLink>
                                    </b>
                                    {saleCampaign.ends
                                        ? ` (until ${new Date(saleCampaign.ends).toLocaleDateString("en-GB", { day: "numeric", month: "short" })})`
                                        : ""}
                                    {product?.campaign_price != null
                                        ? ` this product sells at ${formatCampaignPrice(product.campaign_price)}`
                                        : " this product has the campaign's badge"}
                                    . The discount below is its own: while both run, customers get the lower price,
                                    and this one carries on after the campaign.
                                </Alert>
                            )}
                            {!!product?.price_tiers?.length && (
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    Combo prices:{" "}
                                    <b>
                                        {product.price_tiers
                                            .map((t) => `${t.min_quantity}+ ${formatCampaignPrice(t.unit_price)} each`)
                                            .join(" · ")}
                                    </b>
                                    . Change them from the product's details panel, under Pricing & stock.
                                </Alert>
                            )}
                            <Button
                                type="button"
                                size="small"
                                color="inherit"
                                startIcon={<SaleIcon fontSize="small" />}
                                endIcon={
                                    discountEnabled ? (
                                        <ExpandLessIcon fontSize="small" />
                                    ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                    )
                                }
                                onClick={() => {
                                    const next = !discountEnabled;
                                    setDiscountEnabled(next);
                                    if (!next) {
                                        // Collapsing clears the fields too —
                                        // otherwise a stale value could
                                        // reappear if the section is
                                        // re-expanded without noticing.
                                        setValue("sale_price", undefined);
                                        setValue("sale_starts_at", null);
                                        setValue("sale_ends_at", null);
                                    }
                                }}
                            >
                                {discountEnabled
                                    ? "Remove discounted price"
                                    : "Add a discounted price"}
                            </Button>
                            <Collapse in={discountEnabled}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 2,
                                        mt: 1,
                                    }}
                                >
                                    <Controller
                                        name="sale_price"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                value={field.value ?? ""}
                                                type="number"
                                                margin="normal"
                                                label="Sale Price (৳)"
                                                inputProps={{
                                                    step: 0.01,
                                                    min: 0,
                                                }}
                                                sx={{ minWidth: 180, flex: 1 }}
                                                error={!!errors.sale_price}
                                                helperText={
                                                    errors.sale_price?.message
                                                }
                                            />
                                        )}
                                    />
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <Controller
                                            name="sale_starts_at"
                                            control={control}
                                            render={({ field }) => (
                                                <DateTimePicker
                                                    label="Sale Starts (optional)"
                                                    value={field.value ?? null}
                                                    onChange={field.onChange}
                                                    slotProps={{
                                                        textField: {
                                                            sx: { minWidth: 220, flex: 1 },
                                                            helperText:
                                                                "Leave blank to start immediately",
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                        <Controller
                                            name="sale_ends_at"
                                            control={control}
                                            render={({ field }) => (
                                                <DateTimePicker
                                                    label="Sale Ends (optional)"
                                                    value={field.value ?? null}
                                                    onChange={field.onChange}
                                                    slotProps={{
                                                        textField: {
                                                            sx: { minWidth: 220, flex: 1 },
                                                            error: !!errors.sale_ends_at,
                                                            helperText:
                                                                errors.sale_ends_at?.message ||
                                                                "Leave blank for an ongoing discount",
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Box>
                            </Collapse>
                        </Box>

                        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                            <Controller
                                name="weight"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        margin="normal"
                                        fullWidth
                                        label="Net Weight (kg)"
                                        inputProps={{ step: 0.001, min: 0 }}
                                        error={!!errors.weight}
                                        helperText={errors.weight?.message}
                                    />
                                )}
                            />

                            <Controller
                                name="gross_weight"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        margin="normal"
                                        fullWidth
                                        label="Gross Weight (kg)"
                                        inputProps={{ step: 0.001, min: 0 }}
                                        error={!!errors.gross_weight}
                                        helperText={errors.gross_weight?.message}
                                    />
                                )}
                            />

                            <Controller
                                name="dimensions"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        label="Dimensions"
                                        placeholder="L x W x H"
                                        error={!!errors.dimensions}
                                        helperText={errors.dimensions?.message}
                                    />
                                )}
                            />
                        </Box>

                        {/* Initial Stock Settings - Only show for create mode */}
                        {mode === "create" && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: "flex", gap: 2 }}>
                                    <Controller
                                        name="initial_stock"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="number"
                                                margin="normal"
                                                label="Initial Stock"
                                                placeholder="0"
                                                inputProps={{ min: 0 }}
                                                error={!!errors.initial_stock}
                                                helperText={
                                                    errors.initial_stock
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="min_stock_level"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="number"
                                                margin="normal"
                                                label="Min Stock Level"
                                                placeholder="10"
                                                inputProps={{ min: 0 }}
                                                error={!!errors.min_stock_level}
                                                helperText={
                                                    errors.min_stock_level
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="max_stock_level"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="number"
                                                margin="normal"
                                                label="Max Stock Level"
                                                placeholder="1000"
                                                inputProps={{ min: 1 }}
                                                error={!!errors.max_stock_level}
                                                helperText={
                                                    errors.max_stock_level
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
                            <Controller
                                name="is_active"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                {...field}
                                                checked={field.value}
                                            />
                                        }
                                        label="Active"
                                    />
                                )}
                            />
                            <Controller
                                name="is_featured"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                {...field}
                                                checked={field.value}
                                                color="warning"
                                            />
                                        }
                                        label="Featured on Homepage"
                                    />
                                )}
                            />
                            <Controller
                                name="is_visible"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                {...field}
                                                checked={field.value ?? true}
                                                color="success"
                                            />
                                        }
                                        label="Visible on Website"
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                )}

                {/* Images Tab - Only in edit mode */}
                {mode === "edit" && currentTab === 1 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Product Images
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            Upload and manage product images. The first image
                            will be set as default automatically.
                        </Typography>

                        <ImageUploadZone
                            onUpload={handleImageUpload}
                            maxFiles={10}
                        />

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" gutterBottom>
                            Current Images
                        </Typography>
                        <ProductImageGallery
                            images={productImages}
                            onDelete={handleImageDelete}
                            onSetDefault={handleSetDefaultImage}
                            loading={loadingImages}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit(handleFormSubmit)}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? (
                        <CircularProgress size={24} />
                    ) : mode === "create" ? (
                        "Create"
                    ) : (
                        "Update"
                    )}
                </Button>
            </DialogActions>

            {/* Category Creation Modal */}
            <CreateCategoryModal
                open={showCreateCategory}
                onClose={() => setShowCreateCategory(false)}
                onSuccess={handleCategoryCreated}
                parentCategories={categories}
            />

            {/* Brand Creation Modal */}
            <CreateBrandModal
                open={showCreateBrand}
                onClose={() => setShowCreateBrand(false)}
                onSuccess={handleBrandCreated}
            />
        </Dialog>
    );
};
